import { useCallback, useEffect, useRef, useState } from 'react';
import { useDeviceService } from '@/contexts/DeviceServiceContext';
import { useHarnessStore } from '@/store/useHarnessStore';
import { harnessService } from '@/services/api/harnessService';
import type { AppendCommunicationLogFrame } from '@/services/api/harnessService';
import { impedanceExecToJson, impedanceResultToJson, serializeDevMessage } from '@/lib/protobuf';
import type { ImpedanceExec } from '@/lib/protobuf';
import type { MeasurementResult } from '@/types/device';
import type { CommunicationFrame, MeasurementDto } from '@/features/harness/types';
import logger from '@/lib/logger';

/** How often the accumulation buffer flushes while no Measurement is running (Dev Notes: "a few seconds"). */
const FLUSH_INTERVAL_MS = 5000;

/**
 * Safety cap on the outbound accumulation buffer. Far above MAX_LIVE_FRAMES (500) so it never
 * bites under normal operation — it only bounds memory (and beacon payload size) if the backend
 * is unreachable for a long stretch and flushes keep failing. Overflow drops the OLDEST frames
 * and logs it, rather than growing without bound or silently.
 */
const MAX_PENDING_FRAMES = 5000;

/** An accumulated frame stamped with the Batch it was captured under (see cross-batch note in `flush`). */
interface PendingFrame {
  batchId: string;
  frame: CommunicationFrame;
}

/**
 * Serialize the captured device response for verbatim JSONB persistence.
 * Enum-bearing sub-objects (impedanceResult) go through the shared converters so the
 * stored JSON matches how the rest of the app persists measurement results.
 */
/** Clamp a device-reported progress value to [0, 100], guarding against NaN/Infinity. */
function clampProgress(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

function buildResultJson(result: MeasurementResult): Record<string, unknown> {
  return {
    deviceUid: result.deviceUid,
    deviceSerialNumber: result.deviceSerialNumber,
    firmwareVersion: result.firmwareVersion,
    hardwareVersion: result.hardwareVersion,
    durationMs: result.durationMs,
    errorCode: result.errorCode,
    completedAt:
      result.completedAt instanceof Date
        ? result.completedAt.toISOString()
        : result.completedAt,
    impedanceResult: result.impedanceResult
      ? impedanceResultToJson(result.impedanceResult)
      : null,
  };
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/** Best-effort JSON-safe decode for display/persistence; never throws. */
function decodeSafely(message: unknown): unknown {
  try {
    return JSON.parse(serializeDevMessage(message));
  } catch {
    return null;
  }
}

function toRequestFrame(frame: CommunicationFrame): AppendCommunicationLogFrame {
  return {
    direction: frame.direction === 'sent' ? 'Sent' : 'Received',
    timestamp: frame.timestamp,
    rawBytes: uint8ToBase64(frame.rawBytes),
    decodedJson: frame.decoded,
    measurementId: frame.measurementId,
    crcValid: frame.crcValid,
  };
}

export interface UseHarnessMeasurementResult {
  /**
   * Run a Measurement with the given Excitation Profile against the connected device,
   * then persist it to the active Batch. Resolves with the recorded Measurement (with its
   * server-assigned SequenceNumber) and appends it to the store. Rejects if there is no
   * active Batch or the device/persistence call fails.
   */
  runMeasurement: (exec: ImpedanceExec) => Promise<MeasurementDto | undefined>;
  /** true while a measurement is running or being persisted. */
  isRunning: boolean;
  /** Device progress 0–100 for the in-flight measurement. */
  progress: number;
  error: string | null;
}

/**
 * Orchestrates a single harness Measurement:
 *   build ImpedanceExec → deviceService.runMeasurement(...) → POST the completed
 *   Measurement (profile as sent + captured result) → append to the store.
 *
 * Also owns Story 1.3's communication-log recording: a standing subscription to the device's
 * raw sent/received frame streams feeds both the live-render buffer (`useHarnessStore.liveFrames`,
 * AC1) and a local accumulation buffer flushed to the backend on three triggers — Measurement
 * completion, a short idle interval, and best-effort on page unload (AC3). This is a core
 * architecture decision, not an implementation detail: the live view has no backend
 * involvement (no SignalR/hub) because the frontend is the one talking to the device.
 */
export function useHarnessMeasurement(): UseHarnessMeasurementResult {
  const deviceService = useDeviceService();
  const activeBatch = useHarnessStore((s) => s.activeBatch);
  const appendMeasurement = useHarnessStore((s) => s.appendMeasurement);
  const pushFrame = useHarnessStore((s) => s.pushFrame);
  const retagFrames = useHarnessStore((s) => s.retagFrames);
  const setInProgressMeasurement = useHarnessStore((s) => s.setInProgressMeasurement);

  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Synchronous re-entrancy guard so a rapid double-trigger cannot start two runs
  // before the `isRunning` state re-render disables the control.
  const inFlightRef = useRef(false);

  // Always-current active Batch, readable from the device-event handlers and the
  // beforeunload/pagehide listener without re-subscribing on every Batch change.
  const activeBatchRef = useRef(activeBatch);
  activeBatchRef.current = activeBatch;

  // Local pending id tagging every frame captured while a run is in flight. Frames need a tag
  // the instant they're captured, but the real Measurement id isn't known until the run
  // completes and persists — so frames are tagged with this local id first, then retagged to
  // the real id (or back to null on failure) once the outcome is known.
  const activeTagRef = useRef<string | null>(null);

  // Outbound accumulation buffer — independent of the store's `liveFrames` (display-only, capped).
  // Each entry is stamped with the Batch it was captured under so a Batch switch can never
  // misattribute frames (see `flush`). Frames are removed only once their flush POST succeeds
  // (NFR2: never clear-then-POST).
  const pendingFramesRef = useRef<PendingFrame[]>([]);
  const flushInFlightRef = useRef(false);
  // Set when flush() is called while one is already in flight, so the in-flight loop runs once
  // more afterwards — this makes trigger (a) "flush immediately on completion" actually hold
  // even if an idle flush was mid-POST when the Measurement completed.
  const flushAgainRef = useRef(false);

  const capture = useCallback(
    (direction: 'sent' | 'received', raw: Uint8Array, message: unknown, crcValid = true) => {
      const frame: CommunicationFrame = {
        id: crypto.randomUUID(),
        direction,
        timestamp: new Date().toISOString(),
        rawBytes: raw,
        decoded: decodeSafely(message),
        measurementId: activeTagRef.current,
        crcValid,
      };
      // Live buffer always updates (display-only, reset per Batch). Persistence only accumulates
      // frames captured while a Batch is active — frames outside a Batch have nowhere to belong
      // (AC3 scopes recording to "when the Batch is being worked in").
      pushFrame(frame);
      const batch = activeBatchRef.current;
      if (!batch) return;
      pendingFramesRef.current.push({ batchId: batch.id, frame });
      if (pendingFramesRef.current.length > MAX_PENDING_FRAMES) {
        const overflow = pendingFramesRef.current.length - MAX_PENDING_FRAMES;
        pendingFramesRef.current.splice(0, overflow);
        logger.warn(
          `[useHarnessMeasurement] Communication-log buffer exceeded ${MAX_PENDING_FRAMES}; dropped ${overflow} oldest frame(s) after sustained flush failure.`,
        );
      }
    },
    [pushFrame],
  );

  const flush = useCallback(async (): Promise<void> => {
    // Coalesce concurrent callers: if a flush is already running, ask it to loop once more
    // rather than dropping this request or starting an overlapping POST.
    if (flushInFlightRef.current) {
      flushAgainRef.current = true;
      return;
    }
    flushInFlightRef.current = true;
    try {
      do {
        flushAgainRef.current = false;
        const pending = [...pendingFramesRef.current];
        if (pending.length === 0) break;

        // Group by the Batch each frame was captured under, then POST each group to its OWN
        // Batch — never to whatever Batch happens to be active now. Without this, a Batch
        // switch mid-run (or unflushed frames left over from a stopped Batch) would POST
        // Batch A's frames against Batch B: tagged frames 400, untagged frames silently
        // misattributed.
        const byBatch = new Map<string, PendingFrame[]>();
        for (const entry of pending) {
          const group = byBatch.get(entry.batchId);
          if (group) group.push(entry);
          else byBatch.set(entry.batchId, [entry]);
        }

        for (const [batchId, entries] of byBatch) {
          try {
            await harnessService.appendCommunicationLog(batchId, entries.map((e) => toRequestFrame(e.frame)));
            // Remove only the entries this POST actually persisted; a failed group stays queued.
            pendingFramesRef.current = pendingFramesRef.current.filter((x) => !entries.includes(x));
          } catch (err) {
            logger.warn('[useHarnessMeasurement] Communication-log flush failed, will retry:', err);
          }
        }
      } while (flushAgainRef.current);
    } finally {
      flushInFlightRef.current = false;
    }
  }, []);

  // Standing subscription to the device's raw frame streams — the sole source of live
  // communication data (no backend involvement, FR-6/FR-7). `onReceivedMessage` captures EVERY
  // inbound frame (command ACKs + watchdog/progress), not just the unsolicited subset, so the
  // log is symmetric with the sent side (AC3 "every frame"). Covers both in-Measurement frames
  // (activeTagRef set) and unsolicited frames arriving between Measurements (activeTagRef null).
  useEffect(() => {
    const unsubscribeSent = deviceService.onSentMessage?.((raw, message) => {
      capture('sent', raw, message);
    });
    const unsubscribeReceived = deviceService.onReceivedMessage?.((raw, msg, crcValid) => {
      capture('received', raw, msg, crcValid);
    });
    return () => {
      unsubscribeSent?.();
      unsubscribeReceived?.();
    };
  }, [deviceService, capture]);

  // (b) Short idle-interval flush — catches untagged watchdog frames arriving between
  // Measurements. Skipped while a Measurement is running: its frames are tagged with a local
  // pending id the backend cannot resolve yet, and the completion trigger (a) covers them
  // as soon as it can.
  useEffect(() => {
    const timer = setInterval(() => {
      if (activeTagRef.current) return;
      void flush();
    }, FLUSH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [flush]);

  // (c) Best-effort flush on page unload via sendBeacon — modeled on
  // useBeforeUnloadGuard's beforeunload+pagehide+sendBeacon structure, pointed at the harness
  // communication-log endpoint (which is [AllowAnonymous] since sendBeacon cannot attach the
  // Authorization header).
  useEffect(() => {
    const handlePageHide = () => {
      const pending = pendingFramesRef.current;
      if (pending.length === 0) return;
      if (typeof navigator.sendBeacon !== 'function') return;

      // Group by capture-Batch (same reason as flush) and beacon each group to its own Batch.
      const byBatch = new Map<string, PendingFrame[]>();
      for (const entry of pending) {
        const group = byBatch.get(entry.batchId);
        if (group) group.push(entry);
        else byBatch.set(entry.batchId, [entry]);
      }

      const queued = new Set<PendingFrame>();
      for (const [batchId, entries] of byBatch) {
        const payload = JSON.stringify({ frames: entries.map((e) => toRequestFrame(e.frame)) });
        // sendBeacon returns false when the UA refuses to queue (e.g. payload over the ~64KB
        // limit). Only drop frames it actually accepted — the rest are left buffered (moot on a
        // real unload, but avoids clear-then-lose if the page is only frozen into the bfcache).
        const accepted = navigator.sendBeacon(
          `/api/v1/harness/batches/${batchId}/communication-log`,
          new Blob([payload], { type: 'application/json' }),
        );
        if (accepted) entries.forEach((e) => queued.add(e));
      }
      if (queued.size > 0) {
        pendingFramesRef.current = pendingFramesRef.current.filter((x) => !queued.has(x));
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []);

  const runMeasurement = useCallback(
    async (exec: ImpedanceExec): Promise<MeasurementDto | undefined> => {
      if (!activeBatch) {
        throw new Error('Cannot run a Measurement without an active Batch.');
      }
      if (inFlightRef.current) return undefined;
      inFlightRef.current = true;

      const pendingTag = crypto.randomUUID();
      activeTagRef.current = pendingTag;

      setIsRunning(true);
      setInProgressMeasurement(true);
      setProgress(0);
      setError(null);

      const startedAt = new Date();
      try {
        const result = await deviceService.runMeasurement({
          impedanceExec: exec,
          onProgress: (p) => setProgress(clampProgress(p)),
          onStateUpdate: (_busy, p) => setProgress(clampProgress(p)),
        });

        const measurement = await harnessService.addMeasurement(activeBatch.id, {
          // Profile persisted exactly as sent (no curated subset, FR-11).
          excitationProfileJson: impedanceExecToJson(exec),
          resultJson: buildResultJson(result),
          startedAt: startedAt.toISOString(),
          completedAt:
            result.completedAt instanceof Date
              ? result.completedAt.toISOString()
              : new Date().toISOString(),
        });

        appendMeasurement(measurement);

        // Resolve every frame captured during this run to the real Measurement id, then
        // flush immediately — trigger (a), closing the recording loop for this run.
        retagFrames(pendingTag, measurement.id);
        pendingFramesRef.current = pendingFramesRef.current.map((e) =>
          e.frame.measurementId === pendingTag
            ? { ...e, frame: { ...e.frame, measurementId: measurement.id } }
            : e,
        );
        void flush();

        return measurement;
      } catch (err) {
        // The run never produced a persisted Measurement, so its frames have nothing valid
        // to attribute to — fall back to untagged rather than keeping a dangling local id.
        retagFrames(pendingTag, null);
        pendingFramesRef.current = pendingFramesRef.current.map((e) =>
          e.frame.measurementId === pendingTag
            ? { ...e, frame: { ...e.frame, measurementId: null } }
            : e,
        );

        logger.error('[useHarnessMeasurement] Measurement run/persist failed:', err);
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to run or record the Measurement. Please try again.',
        );
        throw err;
      } finally {
        activeTagRef.current = null;
        inFlightRef.current = false;
        setIsRunning(false);
        setInProgressMeasurement(false);
      }
    },
    [activeBatch, deviceService, appendMeasurement, retagFrames, flush, setInProgressMeasurement],
  );

  return { runMeasurement, isRunning, progress, error };
}
